import { useRecoilState, useRecoilValue } from "recoil";
import { SAND_CAT } from "../config";
import {
  developerAccessTokenState,
  platformState,
  accessTokenState,
  activeScopeState,
} from "../global-state";
import { Amplify, Auth } from "aws-amplify";
import { DEV_AUTH_CONFIG, USER_AUTH_CONFIG } from "../aws-config";
import { useAppInterface } from "../app/app-interface-provider";
import { useRef, useEffect } from "react";

const useApiCall = () => {
  const [accessToken, setAccessToken] = useRecoilState(accessTokenState);
  const activeScope = useRecoilValue(activeScopeState);
  const [developerAccessToken, setDeveloperAccessToken] = useRecoilState(
    developerAccessTokenState
  );
  const platform = useRecoilValue(platformState);
  const appInterface = useAppInterface();

  // Use refs to store current token values
  const accessTokenRef = useRef(accessToken);
  const developerAccessTokenRef = useRef(developerAccessToken);

  // Update refs when tokens change
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    developerAccessTokenRef.current = developerAccessToken;
  }, [developerAccessToken]);

  const apiCall = async (
    endpoint,
    method,
    body = null,
    scopeId = null,
    role = "user",
    retry = 0,
    oUserToken = null,
    oDevToken = null
  ) => {
    if (retry >= 2) {
      return null;
    }
    try {
      const headers = {};
      if (scopeId) {
        headers["ScopeId"] = scopeId;
      } else if (activeScope) {
        headers["ScopeId"] = activeScope;
      }
      const roleMap = {
        user: "Authorization",
        dev: "DeveloperAuthorization",
        org: "OrgAuthorization",
        admin: "AdminAuthorization",
      };

      const roles = role.split(","); // Split the input role string by commas

      if (
        role.includes("dev") ||
        role.includes("org") ||
        role.includes("admin")
      ) {
        const dt = await refreshDeveloperToken();
        developerAccessTokenRef.current = dt;
      }

      const ut = oUserToken ? oUserToken : accessTokenRef.current;
      const dt = oDevToken ? oDevToken : developerAccessTokenRef.current;

      for (const r of roles) {
        if (r in roleMap) {
          const token = r === "user" ? ut : dt;
          if (token) {
            headers[roleMap[r]] = `Bearer ${token || ""}`;
          } else {
            headers[roleMap[r]] = "";
          }
        }
      }

      // Handle app platform using AppInterface
      if (platform.source === "app") {
        try {
          // Prepare body data for native interface
          let bodyData = null;
          if (body) {
            if (body instanceof FormData) {
              // Convert FormData to object for native transmission
              const formDataObj = {};
              for (const [key, value] of body.entries()) {
                formDataObj[key] = value;
              }
              bodyData = JSON.stringify(formDataObj);
            } else {
              bodyData = JSON.stringify(body);
            }
          }

          // Delegate to AppInterface apiCall method
          const response = await appInterface.apiCall(
            endpoint,
            method,
            JSON.stringify(headers),
            bodyData || "",
            SAND_CAT
          );

          if (response.error) {
            if (response.error === "UNAUTHENTICATED") {
              const ut = await refreshUserToken();
              return await apiCall(
                endpoint,
                method,
                body,
                role,
                retry + 1,
                ut,
                developerAccessToken
              );
            }
            throw new Error(response.error);
          }

          return response;
        } catch (error) {
          console.error(`Error during app API call to ${endpoint}:`, error);
          return null;
        }
      } else if (platform.source === "desktop") {
        try {
          // Prepare body data for IPC
          let bodyData = null;
          if (body) {
            if (body instanceof FormData) {
              // Convert FormData to object for IPC transmission
              const formDataObj = {};
              for (const [key, value] of body.entries()) {
                formDataObj[key] = value;
              }
              bodyData = { type: "formData", data: formDataObj };
            } else {
              bodyData = { type: "json", data: body };
            }
          }

          // Call main process via IPC to perform API call
          const response = await appInterface.apiCall(
            endpoint,
            method,
            headers,
            bodyData,
            SAND_CAT
          );

          if (response.error) {
            if (response.error === "UNAUTHENTICATED") {
              const ut = await refreshUserToken();
              return await apiCall(
                endpoint,
                method,
                body,
                role,
                retry + 1,
                ut,
                developerAccessToken
              );
            }
            throw new Error(response.error);
          }

          return response;
        } catch (error) {
          console.error(
            `Error during desktop IPC API call to ${endpoint}:`,
            error
          );
          return null;
        }
      } else {
        // Web platforms use direct fetch API
        const options = {
          method,
          headers,
        };

        if (body instanceof FormData) {
          options.body = body;
        } else if (body) {
          options.body = JSON.stringify(body);
          headers["Content-Type"] = "application/json;charset=UTF-8";
        }

        // Check if the current domain is the development environment
        let baseUrl = SAND_CAT;

        const response = await fetch(`${baseUrl}${endpoint}`, options);
        const jsonData = await response.json();

        if (!response.ok) {
          throw new Error(jsonData);
        }

        if (jsonData.error) {
          if (jsonData.error === "UNAUTHENTICATED") {
            const ut = await refreshUserToken();
            return await apiCall(
              endpoint,
              method,
              body,
              role,
              retry + 1,
              ut,
              developerAccessToken
            );
          }
        }

        return jsonData;
      }
    } catch (error) {
      console.error(`Error during ${method} request to ${endpoint}:`, error);
      return null;
    }
  };

  const refreshUserToken = async () => {
    try {
      if (platform.source === "web") {
        Amplify.configure(USER_AUTH_CONFIG);
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken().getJwtToken();
        setAccessToken(accessToken);
        return accessToken;
      }
    } catch (error) {
      console.error("Error to refresh user token");
    }
  };

  const refreshDeveloperToken = async () => {
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken().getJwtToken();
      setDeveloperAccessToken(accessToken);
      return accessToken;
    } catch (error) {
      // do nothing for developer not login
      return null;
    }
  };

  return { apiCall };
};

export default useApiCall;
